import React, { useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';

const PerformanceChart = () => {
  const [chartType, setChartType] = useState('line');

  // Sample performance data
  const performanceData = [
    { month: 'Jan', revenue: 45000, targets: 40000, matches: 1200, conversion: 3.2 },
    { month: 'Feb', revenue: 52000, targets: 48000, matches: 1450, conversion: 3.5 },
    { month: 'Mar', revenue: 48000, targets: 50000, matches: 1100, conversion: 2.8 },
    { month: 'Apr', revenue: 61000, targets: 55000, matches: 1680, conversion: 4.1 },
    { month: 'May', revenue: 55000, targets: 52000, matches: 1320, conversion: 3.4 },
    { month: 'Jun', revenue: 67000, targets: 60000, matches: 1890, conversion: 4.5 },
    { month: 'Jul', revenue: 72000, targets: 65000, matches: 2100, conversion: 4.8 },
    { month: 'Aug', revenue: 68000, targets: 63000, matches: 1950, conversion: 4.3 }
  ];

  const renderChart = () => {
    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '0.5rem'
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="var(--color-primary)" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="targets" stroke="var(--color-accent)" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '0.5rem'
                }}
              />
              <Legend />
              <Bar dataKey="revenue" fill="var(--color-primary)" radius={[8, 8, 0, 0]} />
              <Bar dataKey="targets" fill="var(--color-accent)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '0.5rem'
                }}
              />
              <Legend />
              <Area type="monotone" dataKey="revenue" fill="var(--color-primary)" stroke="var(--color-primary)" fillOpacity={0.3} />
              <Area type="monotone" dataKey="targets" fill="var(--color-accent)" stroke="var(--color-accent)" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Performance Trend</h3>
          <p className="text-sm text-muted-foreground">Revenue vs. Target Performance (8-Month View)</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant={chartType === 'line' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartType('line')}
            title="Line Chart"
          >
            <Icon name="TrendingUp" size={16} />
          </Button>
          <Button
            variant={chartType === 'bar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartType('bar')}
            title="Bar Chart"
          >
            <Icon name="BarChart3" size={16} />
          </Button>
          <Button
            variant={chartType === 'area' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setChartType('area')}
            title="Area Chart"
          >
            <Icon name="Activity" size={16} />
          </Button>
        </div>
      </div>

      {/* Chart */}
      <div className="mb-6">
        {renderChart()}
      </div>

      {/* Chart Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-border">
        <div>
          <p className="text-sm text-muted-foreground mb-1">Avg Revenue</p>
          <p className="text-lg font-semibold text-foreground">$58,125</p>
          <p className="text-xs text-success">↑ 8.2% vs last period</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">Avg Target</p>
          <p className="text-lg font-semibold text-foreground">$54,125</p>
          <p className="text-xs text-success">↑ Performance: +7.4%</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">Total Matches</p>
          <p className="text-lg font-semibold text-foreground">12,490</p>
          <p className="text-xs text-success">↑ 15% vs last period</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">Avg Conversion</p>
          <p className="text-lg font-semibold text-foreground">3.83%</p>
          <p className="text-xs text-success">↑ 12% vs last period</p>
        </div>
      </div>
    </div>
  );
};

export default PerformanceChart;
